import { useState } from "react"
import { Button } from "react-bootstrap"
import { Link } from "react-router-dom"
import { ConfirmModal } from "../../Modals/confirmModal"
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import { useEditBookingMutation, useLazyGetBukiblesQuery, useLazyGetBukinsQuery, useAddNewBukinMutation } from "../../../services/bClientApi"
import canAccess from "../../../services/accessService/accessService"
import { convertStringToJSON } from "../../../Helpers/Strings/stringHelpers"

interface ParkingEntry {
  productId: string
  bukibleId: string
  bukinId: string
  parkingStart: string
  parkingEnd: string
}

interface ParkingSettings {
  parkingType: string
  parkingGroups: string[]
  parkingCost: number
}

interface BookingParkingProps {
  bookingData: any
  propertyData: any
}

export default function BookingParking({ bookingData, propertyData }: BookingParkingProps) {
  const dispatch = useAppDispatch()
  const [purchaseParkingModal, setPurchaseParkingModal] = useState(false)

  // Parse parking settings from property data
  let parkingSettings: ParkingSettings = {
    parkingType: '',
    parkingGroups: [],
    parkingCost: 0,
  }

  if (propertyData?.linkedServices) {
    propertyData.linkedServices.forEach((service: any) => {
      const { linkedService, linkedServiceId } = service
      if (linkedService === 'parking') {
        parkingSettings = convertStringToJSON(linkedServiceId)
      }
    })
  }

  // Extract parking entries from booking data
  const parkingEntries: ParkingEntry[] = []
  if (bookingData && bookingData.linkedServices && bookingData.linkedServices.length > 0) {
    bookingData.linkedServices.forEach((service: any) => {
      if (service.linkedServiceId) {
        const linkedServicePayload = convertStringToJSON(service.linkedServiceId)
        if (linkedServicePayload && linkedServicePayload?.productId === 'parking') {
          parkingEntries.push(linkedServicePayload)
        }
      }
    })
  }

  const [editBooking] = useEditBookingMutation()
  const [addNewBukin] = useAddNewBukinMutation()
  const [findBukibles] = useLazyGetBukiblesQuery()
  const [findBukins] = useLazyGetBukinsQuery()

  const giveParking = async () => {
    setPurchaseParkingModal(false)

    const bukibleKeys: { [key: string]: { data: any, quantity: number } } = {}
    const availableBukiblesResponse = await findBukibles({
      bukiblesQuery: {
        groups: parkingSettings.parkingGroups ? parkingSettings.parkingGroups : ['default'],
      },
      pagination: {
        limit: 100,
        page: 1
      }
    })
    const availableBukibles = availableBukiblesResponse?.data || {}
    
    if (availableBukibles && availableBukibles?.docs?.length > 0) {
      availableBukibles?.docs?.forEach((bukible: any) => {
        bukibleKeys[bukible._id] = {
          data: { ...bukible },
          quantity: bukible.quantity ? bukible.quantity : 0
        }
      })
    }

    const bukinsInRangeResponse = await findBukins({
      bukinsQuery: {
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        bukbleId: [...Object.keys(bukibleKeys)],
        status: ['new', 'confirmed', 'pending', 'request']
      },
      pagination: {
        limit: 100,
        page: 1
      }
    })
    const bukinsInRange = bukinsInRangeResponse?.data || {}
    if (bukinsInRange && bukinsInRange.docs.length > 0) {
      bukinsInRange.docs.forEach((bukin: any) => {
        if (bukin.bukibleId) {
          if (bukibleKeys[bukin.bukibleId]) {
            bukibleKeys[bukin.bukibleId].quantity -= 1
            if (bukibleKeys[bukin.bukibleId].quantity <= 0) {
              delete bukibleKeys[bukin.bukibleId]
            }
          }
        }
      })
    }

    if (Object.keys(bukibleKeys).length === 0) {
      dispatch(showError('No parking available'))
      return {
        error: 'No parking available',
      }
    }

    const firstBukibleId = Object.keys(bukibleKeys)[0]
    const newBukin: any = {
      checkIn: bookingData.checkIn,
      checkOut: bookingData.checkOut,
      bukibleId: firstBukibleId,
      referenceNumber: bookingData.sourceConfirmation,
      bookingId: bookingData._id,
      earnings: 0,
      contactInfo: {
        firstName: bookingData?.contactInfo?.firstName,
        lastName: bookingData?.contactInfo?.lastName,
        email: bookingData?.contactInfo?.email,
        phone: bookingData?.contactInfo?.phone,
      },
      status: 'confirmed',
      notes: '',
    }

    const createdBukin = await addNewBukin(newBukin).unwrap()

    if (!createdBukin || !createdBukin._id) {
      dispatch(showError('Error creating bukin'))
      return {
        error: 'Error creating bukin'
      }
    }

    const linkedServiceEntry = {
      linkedService: `parking-${firstBukibleId}-${createdBukin._id}`,
      linkedServiceId: JSON.stringify({
        productId: 'parking',
        bukibleId: firstBukibleId,
        bukinId: createdBukin._id,
        parkingStart: bookingData.checkIn,
        parkingEnd: bookingData.checkOut,
      }),
    }

    const updatedBooking = await editBooking({
      bookingId: bookingData._id,
      bookingInfo: {
        linkedServices: [
          ...bookingData.linkedServices,
          linkedServiceEntry
        ]
      }
    }).unwrap()

    if (!updatedBooking || !updatedBooking._id) {
      dispatch(showError('Error updating booking'))
      return {
        error: 'Error updating booking'
      }
    }

    dispatch(showSuccess('Parking Purchased'))
  }

  return (
    <>
      {canAccess('routes', 'edit-booking', { payload: bookingData }) && canAccess('routes', 'edit-bukin') && parkingSettings && parkingSettings?.parkingGroups?.length > 0 && (
        <div className="mb-4">
          <h3>Parking Is Offered</h3>
          <ConfirmModal
            title="Purchase Parking"
            message="Would you like to purchase parking?"
            active={purchaseParkingModal}
            onClose={() => setPurchaseParkingModal(false)}
            onConfirm={giveParking}
          />
          <Button
            variant="primary"
            onClick={() => setPurchaseParkingModal(true)}
          >
            Give Parking
          </Button>
        </div>
      )}

      {canAccess('routes', 'bukins') && parkingEntries && parkingEntries.length > 0 && (
        <>
          <h3>Parking Information</h3>
          {parkingEntries.map((entry, idx: number) => (
            <div key={`parking-entry-${idx}`} className="mb-3">
              <b>Parking Start:</b> {entry.parkingStart}<br />
              <b>Parking End:</b> {entry.parkingEnd}<br />
              <b>Bukible Id:</b> {entry.bukibleId}<br />
              <b>Bukin Id:</b> {canAccess('routes', 'bukin') ? (<Link to={`/bukin/${entry.bukinId}`}>{entry.bukinId}</Link>) : `${entry.bukinId}`} <br />
            </div>
          ))}
        </>
      )}
    </>
  )
}
